"use client";

import React, { useState, useEffect, useRef } from "react";
import { cn } from "@/lib/utils";

export const DualScrollWrapper = ({ children, className }: { children: React.ReactNode; className?: string }) => {
  const topRef = useRef<HTMLDivElement>(null);
  const bottomRef = useRef<HTMLDivElement>(null);
  const [contentWidth, setContentWidth] = useState(0);
  const [hasOverflow, setHasOverflow] = useState(false);
  const isSyncing = useRef(false);

  useEffect(() => {
    const updateDimensions = () => {
      if (bottomRef.current) {
        const scrollW = bottomRef.current.scrollWidth;
        const clientW = bottomRef.current.clientWidth;
        setContentWidth(scrollW);
        setHasOverflow(scrollW > clientW + 2);
      }
    };

    const observer = new ResizeObserver(updateDimensions);

    if (bottomRef.current) {
      observer.observe(bottomRef.current);
      if (bottomRef.current.firstElementChild) {
        observer.observe(bottomRef.current.firstElementChild);
      }
    }

    updateDimensions();

    window.addEventListener("resize", updateDimensions);
    return () => {
      observer.disconnect();
      window.removeEventListener("resize", updateDimensions);
    };
  }, []);

  // Keep scrollbars in sync when contentWidth changes
  useEffect(() => {
    if (topRef.current && bottomRef.current) {
      const currentScroll = bottomRef.current.scrollLeft;
      if (topRef.current.scrollLeft !== currentScroll) {
        topRef.current.scrollLeft = currentScroll;
      }
    }
  }, [contentWidth]);

  const onTopScroll = () => {
    if (isSyncing.current) return;
    if (topRef.current && bottomRef.current) {
      isSyncing.current = true;
      bottomRef.current.scrollLeft = topRef.current.scrollLeft;
      requestAnimationFrame(() => {
        isSyncing.current = false;
      });
    }
  };

  const onBottomScroll = () => {
    if (isSyncing.current) return;
    if (topRef.current && bottomRef.current) {
      isSyncing.current = true;
      topRef.current.scrollLeft = bottomRef.current.scrollLeft;
      requestAnimationFrame(() => {
        isSyncing.current = false;
      });
    }
  };

  return (
    <div className={cn("flex flex-col w-full flex-1 min-w-0", className)}>
      {/* Target Dummy Top Scrollbar (shows when content overflows horizontally) */}
      <div
        ref={topRef}
        onScroll={onTopScroll}
        className={cn(
          "overflow-x-auto w-full transition-all duration-150 z-20 select-none [&::-webkit-scrollbar]:h-2.5 [&::-webkit-scrollbar-track]:bg-[#eef4f5] [&::-webkit-scrollbar-thumb]:bg-[#007a8a]/50 [&::-webkit-scrollbar-thumb]:rounded-full hover:[&::-webkit-scrollbar-thumb]:bg-[#007a8a]",
          hasOverflow ? "h-2.5 border-b border-[#dce8e6] bg-[#f7faf9]" : "h-0 overflow-hidden"
        )}
      >
        <div style={{ width: contentWidth, height: '1px' }} />
      </div>
      {/* Original Content */}
      <div
        ref={bottomRef}
        onScroll={onBottomScroll}
        className="overflow-x-auto w-full flex-1 [&::-webkit-scrollbar]:h-2.5 [&::-webkit-scrollbar-track]:bg-[#eef4f5] [&::-webkit-scrollbar-thumb]:bg-[#007a8a]/50 [&::-webkit-scrollbar-thumb]:rounded-full hover:[&::-webkit-scrollbar-thumb]:bg-[#007a8a]"
      >
        {children}
      </div>
    </div>
  );
};
